import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

export type AppDropdownScrollEdge = {
    atTop: boolean;
    atBottom: boolean;
    overflow: boolean;
};

/**
 * Native scroll + top/bottom fade hints, boundary pulse, wheel containment (matches header dropdowns).
 */
export function useAppDropdownScroll(
    isOpen: boolean,
    scrollGateBlocked: boolean,
    relayoutKey: unknown,
) {
    const scrollRef = useRef<HTMLDivElement | null>(null);
    const sampleRef = useRef<{ top: number; initialized: boolean }>({
        top: 0,
        initialized: false,
    });
    const [edge, setEdge] = useState<AppDropdownScrollEdge>({
        atTop: true,
        atBottom: true,
        overflow: false,
    });
    const [pulseTop, setPulseTop] = useState(false);
    const [pulseBottom, setPulseBottom] = useState(false);

    const syncScrollEdges = useCallback(() => {
        const el = scrollRef.current;
        if (!el) {
            return;
        }
        const { scrollTop, scrollHeight, clientHeight } = el;
        const slack = 3;
        const atTop = scrollTop <= slack;
        const atBottom = scrollTop + clientHeight >= scrollHeight - slack;
        const overflow = scrollHeight > clientHeight + slack;
        const sample = sampleRef.current;
        if (!sample.initialized) {
            sample.top = scrollTop;
            sample.initialized = true;
            setEdge({ atTop, atBottom, overflow });
            return;
        }
        const prevTop = sample.top;
        let nextPulseTop = false;
        let nextPulseBottom = false;
        if (overflow) {
            if (atTop && prevTop > slack) {
                nextPulseTop = true;
            }
            if (atBottom && prevTop < scrollHeight - clientHeight - slack) {
                nextPulseBottom = true;
            }
        }
        sample.top = scrollTop;
        setEdge({ atTop, atBottom, overflow });
        if (nextPulseTop) {
            setPulseTop(true);
            window.setTimeout(() => setPulseTop(false), 550);
        }
        if (nextPulseBottom) {
            setPulseBottom(true);
            window.setTimeout(() => setPulseBottom(false), 550);
        }
    }, []);

    useEffect(() => {
        if (!isOpen) {
            sampleRef.current = { top: 0, initialized: false };
        }
    }, [isOpen]);

    useLayoutEffect(() => {
        if (!isOpen || scrollGateBlocked) {
            return;
        }
        syncScrollEdges();
    }, [isOpen, scrollGateBlocked, relayoutKey, syncScrollEdges]);

    useEffect(() => {
        if (!isOpen) {
            return;
        }
        const el = scrollRef.current;
        if (!el) {
            return;
        }
        const onWheel = (e: WheelEvent) => {
            const { scrollTop, scrollHeight, clientHeight } = el;
            const delta = e.deltaY;
            const edgeSlack = 2;
            const atTop = scrollTop <= edgeSlack;
            const atBottom = scrollTop + clientHeight >= scrollHeight - edgeSlack;
            if ((delta < 0 && atTop) || (delta > 0 && atBottom)) {
                e.preventDefault();
                e.stopPropagation();
            }
        };
        el.addEventListener("wheel", onWheel, { passive: false });
        return () => el.removeEventListener("wheel", onWheel);
    }, [isOpen, scrollGateBlocked]);

    useEffect(() => {
        if (!isOpen || scrollGateBlocked) {
            return;
        }
        const el = scrollRef.current;
        if (!el || typeof ResizeObserver === "undefined") {
            return;
        }
        const ro = new ResizeObserver(() => syncScrollEdges());
        ro.observe(el);
        return () => ro.disconnect();
    }, [isOpen, scrollGateBlocked, syncScrollEdges]);

    return { scrollRef, edge, pulseTop, pulseBottom, syncScrollEdges };
}
